/**
 * mountApp — choose between ``ReactDOM.hydrate`` and
 * ``ReactDOM.render`` based on whether the root element already
 * has prerendered DOM content.
 *
 * Why this lives in its own module: the previous direct
 * ``ReactDOM.render(...)`` call in index.js was untestable, and
 * the conditional that preceded it (which we need when
 * react-snap prerenders public routes) is exactly the kind of
 * subtle decision that should be exercised by a unit test
 * rather than learned about in production.
 *
 * The contract:
 *   - root is empty (SPA shell with no children) → ``render``.
 *     This is the case for transactional routes like
 *     /select-report/<uuid> that S3 falls back to via 200.html.
 *   - root has children (prerendered HTML written by react-snap
 *     into build/<route>/index.html) → ``hydrate``.
 *     React reuses the existing DOM, attaches event handlers,
 *     and avoids the flash of re-rendering.
 *
 * The earlier blank-page bug happened because S3 fell back to
 * the wrong file (the prerendered homepage's index.html instead
 * of an empty 200.html shell), so root.hasChildNodes() was true
 * but the children were homepage markup, not the route's.
 * That's an infra bug — fixed by routing to 200.html on misses
 * — not a bug in this function.  This function's job is to
 * follow the contract: hydrate iff DOM has content.
 */
function mountApp(app, rootElement, ReactDOMImpl) {
  if (!rootElement) {
    throw new Error("mountApp: missing root element");
  }
  if (rootElement.hasChildNodes()) {
    ReactDOMImpl.hydrate(app, rootElement);
    return "hydrate";
  }
  ReactDOMImpl.render(app, rootElement);
  return "render";
}

module.exports = mountApp;
module.exports.default = mountApp;
